import {
  Archive,
  Cloud,
  Download,
  Folder,
  LoaderCircle,
  Mic,
  MoreHorizontal,
  RotateCcw,
  Search,
  SquarePen,
  Square,
  Trash2
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PcmChunkRecorder, resampleTo16Khz, rmsToPercent } from "./lib/audio";
import {
  createLocalDocument,
  deleteDocument,
  deleteDocuments,
  listDocuments,
  saveDocument,
  type DocumentStatus,
  type LocalDocument
} from "./lib/documents";
import {
  deleteDriveDocument,
  deleteDriveDocuments,
  isGoogleDriveConfigured,
  listDriveDocuments,
  requestDriveAccess,
  saveDriveDocument
} from "./lib/googleDrive";
import type {
  AudioChunk,
  InferenceDevice,
  ModelConfig,
  TranscriptEntry,
  WorkerOutboundMessage,
  WorkerPhase
} from "./types";

const DEFAULT_MODEL_ID = import.meta.env.VITE_DEFAULT_MODEL_ID || "Xenova/whisper-tiny.en";
const DEFAULT_REVISION = "main";
const DEFAULT_MODEL_BASE_URL = import.meta.env.VITE_MODEL_BASE_URL || "";
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";
const DEFAULT_TITLE = "Untitled";
const STORAGE_TITLE_KEY = "whisperwriter.documentTitle";
const STORAGE_TEXT_KEY = "whisperwriter.documentText";
const LEGACY_STORAGE_TITLE_KEY = "cloudwrite.documentTitle";
const LEGACY_STORAGE_TEXT_KEY = "cloudwrite.documentText";
const ACTIVE_DOCUMENT_KEY = "whisperwriter.activeDocumentId";
const QUIET_RMS_THRESHOLD = 0.006;
const SPEECH_RMS_THRESHOLD = 0.012;
const AUTO_FINISH_SILENCE_MS = 2800;
const NO_SPEECH_TIMEOUT_MS = 8000;
const RECORDING_SLICE_SECONDS = 0.5;
const PAUSE_BREAK_SECONDS = 0.85;
const PARAGRAPH_PAUSE_SECONDS = 1.75;
const PAUSE_RMS_THRESHOLD = 0.009;
const SEGMENT_MIN_SECONDS = 10;
const SEGMENT_TARGET_SECONDS = 22;
const SEGMENT_MAX_SECONDS = 28;
const MAX_RECORDING_SECONDS = 5 * 60;
const COUNTDOWN_WARNING_SECONDS = 30;
const VOICE_DEVICE: InferenceDevice = "wasm";

type AppendMode = "paragraph" | "auto" | "join";
type LibraryView = "documents" | "archive" | "trash";
type StorageMode = "local" | "drive";
type DriveStatus = "unavailable" | "available" | "connecting" | "ready";

interface TranscriptionSegment {
  samples: Float32Array;
  durationSeconds: number;
  rms: number;
  appendMode: AppendMode;
}

interface SegmentBoundary {
  endIndex: number;
  reason: "final" | "forced" | "natural";
  pauseSeconds: number;
}

interface PauseCandidate {
  endIndex: number;
  pauseSeconds: number;
  score: number;
}

function readStoredValue(key: string, defaultValue: string, legacyKeys: string[] = []): string {
  try {
    const storedValue = window.localStorage?.getItem(key);
    if (storedValue !== null && storedValue !== undefined) {
      return storedValue;
    }

    for (const legacyKey of legacyKeys) {
      const legacyValue = window.localStorage?.getItem(legacyKey);
      if (legacyValue !== null && legacyValue !== undefined) {
        writeStoredValue(key, legacyValue);
        return legacyValue;
      }
    }

    return defaultValue;
  } catch {
    return defaultValue;
  }
}

function writeStoredValue(key: string, value: string): void {
  try {
    window.localStorage?.setItem(key, value);
  } catch {
    // Storage can be unavailable in strict browser contexts. The editor still works in memory.
  }
}

function buildTranscriptionSegments(chunks: AudioChunk[]): TranscriptionSegment[] {
  const segments: Array<{ chunks: AudioChunk[]; appendMode: AppendMode }> = [];
  let startIndex = findNextSpeechIndex(chunks, 0);
  let appendMode: AppendMode = "paragraph";

  while (startIndex < chunks.length) {
    const boundary = chooseSegmentBoundary(chunks, startIndex);
    const trimmed = trimQuietEdges(chunks.slice(startIndex, boundary.endIndex));
    if (containsSpeech(trimmed)) {
      segments.push({
        chunks: trimmed,
        appendMode
      });
    }
    appendMode = appendModeAfterBoundary(boundary);
    startIndex = findNextSpeechIndex(chunks, Math.max(boundary.endIndex, startIndex + 1));
  }

  return segments.map((segment) => {
    const samples = concatenateSamples(segment.chunks);
    const durationSeconds = samples.length / 16_000;
    return {
      samples,
      durationSeconds,
      rms: calculateRms(segment.chunks),
      appendMode: segment.appendMode
    };
  });
}

function findNextSpeechIndex(chunks: AudioChunk[], fromIndex: number): number {
  let index = fromIndex;

  while (index < chunks.length && (chunks[index]?.rms ?? 0) < QUIET_RMS_THRESHOLD) {
    index += 1;
  }

  return index;
}

function chooseSegmentBoundary(chunks: AudioChunk[], startIndex: number): SegmentBoundary {
  const hardEndIndex = findDurationBoundary(chunks, startIndex, SEGMENT_MAX_SECONDS);

  if (hardEndIndex >= chunks.length) {
    return {
      endIndex: chunks.length,
      reason: "final",
      pauseSeconds: 0
    };
  }

  const pauseBoundary = findPauseBoundary(chunks, startIndex, hardEndIndex);
  if (pauseBoundary) {
    return pauseBoundary;
  }

  return {
    endIndex: findLowestEnergyBoundary(chunks, startIndex, hardEndIndex),
    reason: "forced",
    pauseSeconds: 0
  };
}

function appendModeAfterBoundary(boundary: SegmentBoundary): AppendMode {
  if (boundary.reason === "forced") {
    return "join";
  }

  if (boundary.reason === "natural" && boundary.pauseSeconds >= PARAGRAPH_PAUSE_SECONDS) {
    return "paragraph";
  }

  return "auto";
}

function findDurationBoundary(chunks: AudioChunk[], startIndex: number, maxSeconds: number): number {
  let durationSeconds = 0;

  for (let index = startIndex; index < chunks.length; index += 1) {
    durationSeconds += chunks[index]?.durationSeconds ?? 0;

    if (durationSeconds >= maxSeconds) {
      return index + 1;
    }
  }

  return chunks.length;
}

function findPauseBoundary(
  chunks: AudioChunk[],
  startIndex: number,
  hardEndIndex: number
): SegmentBoundary | null {
  let elapsedSeconds = 0;
  let quietSeconds = 0;
  let quietEnergy = 0;
  let bestCandidate: PauseCandidate | null = null;

  const considerPause = (endIndex: number, elapsedAtPauseEnd: number) => {
    if (quietSeconds < PAUSE_BREAK_SECONDS || elapsedAtPauseEnd < SEGMENT_MIN_SECONDS) {
      return;
    }

    const averageRms = quietEnergy / Math.max(quietSeconds, Number.EPSILON);
    const pauseDepth = Math.max(0, PAUSE_RMS_THRESHOLD - averageRms) / PAUSE_RMS_THRESHOLD;
    const targetPenalty = Math.abs(elapsedAtPauseEnd - SEGMENT_TARGET_SECONDS) * 0.04;
    const earlyPenalty = elapsedAtPauseEnd < SEGMENT_TARGET_SECONDS ? 0.2 : 0;
    const candidate: PauseCandidate = {
      endIndex,
      pauseSeconds: quietSeconds,
      score: quietSeconds * 4 + pauseDepth - targetPenalty - earlyPenalty
    };

    if (!bestCandidate || candidate.score > bestCandidate.score) {
      bestCandidate = candidate;
    }
  };

  for (let index = startIndex; index < hardEndIndex; index += 1) {
    const chunk = chunks[index];
    if (!chunk) {
      continue;
    }

    elapsedSeconds += chunk.durationSeconds;

    if (chunk.rms < PAUSE_RMS_THRESHOLD) {
      quietSeconds += chunk.durationSeconds;
      quietEnergy += chunk.rms * chunk.durationSeconds;
    } else {
      considerPause(index, elapsedSeconds - chunk.durationSeconds);
      quietSeconds = 0;
      quietEnergy = 0;
    }
  }

  if (quietSeconds > 0) {
    considerPause(hardEndIndex, elapsedSeconds);
  }

  const candidate = bestCandidate as PauseCandidate | null;

  return candidate
    ? {
        endIndex: candidate.endIndex,
        reason: "natural",
        pauseSeconds: candidate.pauseSeconds
      }
    : null;
}

function findLowestEnergyBoundary(
  chunks: AudioChunk[],
  startIndex: number,
  hardEndIndex: number
): number {
  let elapsedSeconds = 0;
  let bestBoundary = hardEndIndex;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let index = startIndex + 1; index < hardEndIndex; index += 1) {
    elapsedSeconds += chunks[index - 1]?.durationSeconds ?? 0;

    if (elapsedSeconds < SEGMENT_MIN_SECONDS) {
      continue;
    }

    const before = chunks[index - 1]?.rms ?? 0;
    const after = chunks[index]?.rms ?? before;
    const energyScore = (before + after) / 2;
    const targetPenalty = Math.abs(elapsedSeconds - SEGMENT_TARGET_SECONDS) * 0.0002;
    const score = energyScore + targetPenalty;

    if (score < bestScore) {
      bestScore = score;
      bestBoundary = index;
    }
  }

  return Math.max(startIndex + 1, bestBoundary);
}

function trimQuietEdges(chunks: AudioChunk[]): AudioChunk[] {
  let start = 0;
  let end = chunks.length;

  while (start < end && (chunks[start]?.rms ?? 0) < QUIET_RMS_THRESHOLD) {
    start += 1;
  }

  while (end > start && (chunks[end - 1]?.rms ?? 0) < QUIET_RMS_THRESHOLD) {
    end -= 1;
  }

  return chunks.slice(start, end);
}

function containsSpeech(chunks: AudioChunk[]): boolean {
  return chunks.some((chunk) => chunk.rms >= QUIET_RMS_THRESHOLD);
}

function concatenateSamples(chunks: AudioChunk[]): Float32Array {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.samples.length, 0);
  const output = new Float32Array(totalLength);
  let offset = 0;

  for (const chunk of chunks) {
    output.set(chunk.samples, offset);
    offset += chunk.samples.length;
  }

  return output;
}

function calculateRms(chunks: AudioChunk[]): number {
  let weightedEnergy = 0;
  let totalSamples = 0;

  for (const chunk of chunks) {
    weightedEnergy += chunk.rms * chunk.rms * chunk.samples.length;
    totalSamples += chunk.samples.length;
  }

  return Math.sqrt(weightedEnergy / Math.max(1, totalSamples));
}

function startsWithLowercaseWord(text: string): boolean {
  const firstLetter = text.match(/[A-Za-z]/)?.[0];

  return Boolean(firstLetter && firstLetter === firstLetter.toLowerCase());
}

function endsWithSentencePunctuation(text: string): boolean {
  return /[.!?][)"'\]]*$/.test(text.trimEnd());
}

function shouldAppendInline(previous: string, next: string, appendMode: AppendMode): boolean {
  if (appendMode === "join" || appendMode === "auto") {
    return true;
  }

  return startsWithLowercaseWord(next) || !endsWithSentencePunctuation(previous);
}

function joinTranscriptText(previous: string, next: string): string {
  const previousText = startsWithLowercaseWord(next)
    ? previous.trimEnd().replace(/[.!?]+([)"'\]]*)$/, "$1")
    : previous.trimEnd();
  const separator = /^[,.;:!?]/.test(next) ? "" : " ";

  return `${previousText}${separator}${next}`;
}

function formatCountdown(seconds: number): string {
  const clampedSeconds = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(clampedSeconds / 60);
  const remainingSeconds = clampedSeconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

function formatDocumentDate(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(new Date(timestamp));
}

function formatExportDate(timestamp = Date.now()): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function getDocumentDisplayTitle(document: Pick<LocalDocument, "title" | "createdAt">): string {
  return document.title.trim() || formatDocumentDate(document.createdAt);
}

function getDocumentPreview(text: string): string {
  return text.replace(/\s+/g, " ").trim().slice(0, 260);
}

function getDocumentStatus(document: Pick<LocalDocument, "status">): DocumentStatus {
  return document.status ?? "active";
}

function getLibraryTitle(libraryView: LibraryView): string {
  if (libraryView === "archive") {
    return "Archive";
  }

  if (libraryView === "trash") {
    return "Trash folder";
  }

  return "Documents";
}

function getLibraryCountLabel(libraryView: LibraryView, count: number): string {
  if (libraryView === "archive") {
    return count === 1 ? "1 archived" : `${count} archived`;
  }

  if (libraryView === "trash") {
    return count === 1 ? "1 in trash" : `${count} in trash`;
  }

  return count === 1 ? "1 document" : `${count} documents`;
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function safeFileName(value: string): string {
  return (value.trim() || "whisperwriter").replace(/[^\w.-]+/g, "-");
}

export function App() {
  const workerRef = useRef<Worker | null>(null);
  const recorderRef = useRef<PcmChunkRecorder | null>(null);
  const pendingIdsRef = useRef(new Set<string>());
  const segmentAppendModeRef = useRef(new Map<string, AppendMode>());
  const recordedChunksRef = useRef<AudioChunk[]>([]);
  const documentsRef = useRef<LocalDocument[]>([]);
  const storageModeRef = useRef<StorageMode>("local");
  const segmentSequenceRef = useRef(0);
  const finishingRef = useRef(false);
  const preparedRef = useRef(false);
  const documentsReadyRef = useRef(false);
  const documentRef = useRef<HTMLTextAreaElement | null>(null);
  const stageRef = useRef<HTMLElement | null>(null);
  const micActiveRef = useRef(false);
  const speechHeardRef = useRef(false);
  const silenceTimerRef = useRef<number | undefined>(undefined);
  const noSpeechTimerRef = useRef<number | undefined>(undefined);
  const recordingLimitTimerRef = useRef<number | undefined>(undefined);

  const [documents, setDocuments] = useState<LocalDocument[]>([]);
  const [currentDocumentId, setCurrentDocumentId] = useState("");
  const [documentTitle, setDocumentTitle] = useState("");
  const [documentText, setDocumentText] = useState("");
  const [viewMode, setViewMode] = useState<"editor" | "library">("editor");
  const [libraryView, setLibraryView] = useState<LibraryView>("documents");
  const [searchTerm, setSearchTerm] = useState("");
  const [actionMenuDocumentId, setActionMenuDocumentId] = useState("");
  const [documentsReady, setDocumentsReady] = useState(false);
  const [libraryError, setLibraryError] = useState("");
  const [storageMode, setStorageMode] = useState<StorageMode>("local");
  const [driveStatus, setDriveStatus] = useState<DriveStatus>(
    isGoogleDriveConfigured(GOOGLE_CLIENT_ID) ? "available" : "unavailable"
  );
  const [micActive, setMicActive] = useState(false);
  const [micError, setMicError] = useState("");
  const [workerPhase, setWorkerPhase] = useState<WorkerPhase>("idle");
  const [workerError, setWorkerError] = useState("");
  const [level, setLevel] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [transcripts, setTranscripts] = useState<TranscriptEntry[]>([]);
  const [remainingRecordingSeconds, setRemainingRecordingSeconds] = useState<number | null>(null);

  const modelConfig = useMemo<ModelConfig>(
    () => ({
      modelId: DEFAULT_MODEL_ID,
      modelBaseUrl: DEFAULT_MODEL_BASE_URL,
      revision: DEFAULT_REVISION,
      device: VOICE_DEVICE
    }),
    []
  );

  const currentDocument = useMemo(
    () => documents.find((document) => document.id === currentDocumentId) ?? null,
    [currentDocumentId, documents]
  );

  const documentStats = useMemo(() => {
    return {
      words: countWords(documentText)
    };
  }, [documentText]);

  const activeDocuments = useMemo(
    () => documents.filter((document) => getDocumentStatus(document) === "active"),
    [documents]
  );

  const archivedDocuments = useMemo(
    () => documents.filter((document) => getDocumentStatus(document) === "archived"),
    [documents]
  );

  const trashedDocuments = useMemo(
    () => documents.filter((document) => getDocumentStatus(document) === "trashed"),
    [documents]
  );

  const libraryDocuments = useMemo(() => {
    if (libraryView === "archive") {
      return archivedDocuments;
    }

    if (libraryView === "trash") {
      return trashedDocuments;
    }

    return activeDocuments;
  }, [activeDocuments, archivedDocuments, libraryView, trashedDocuments]);

  const filteredDocuments = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return libraryDocuments;
    }

    return libraryDocuments.filter((document) => {
      const displayTitle = getDocumentDisplayTitle(document).toLowerCase();
      return (
        displayTitle.includes(query) ||
        document.title.toLowerCase().includes(query) ||
        document.text.toLowerCase().includes(query)
      );
    });
  }, [libraryDocuments, searchTerm]);

  const countdownSeconds =
    micActive &&
    remainingRecordingSeconds !== null &&
    remainingRecordingSeconds <= COUNTDOWN_WARNING_SECONDS
      ? remainingRecordingSeconds
      : null;

  const voiceActionClass = [
    "voice-action",
    micActive ? "active" : "",
    countdownSeconds !== null ? "warning" : ""
  ]
    .filter(Boolean)
    .join(" ");

  const titleInputSize = Math.max(8, Math.min(48, documentTitle.length || DEFAULT_TITLE.length));

  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  useEffect(() => {
    storageModeRef.current = storageMode;
  }, [storageMode]);

  useEffect(() => {
    setActionMenuDocumentId("");
  }, [libraryView, searchTerm, viewMode]);

  useEffect(() => {
    if (!actionMenuDocumentId) {
      return;
    }

    const closeMenu = () => setActionMenuDocumentId("");
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    window.addEventListener("click", closeMenu);
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      window.removeEventListener("click", closeMenu);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [actionMenuDocumentId]);

  useEffect(() => {
    let cancelled = false;

    const loadLocalDocuments = async () => {
      try {
        let localDocuments = await listDocuments();

        if (localDocuments.length === 0) {
          const legacyTitle = readStoredValue(STORAGE_TITLE_KEY, "", [LEGACY_STORAGE_TITLE_KEY]);
          const legacyText = readStoredValue(STORAGE_TEXT_KEY, "", [LEGACY_STORAGE_TEXT_KEY]);
          const migratedDocument = createLocalDocument({
            title: legacyTitle.trim() && legacyTitle !== DEFAULT_TITLE ? legacyTitle : "",
            text: legacyText
          });
          await saveDocument(migratedDocument);
          localDocuments = [migratedDocument];
        }

        const activeDocumentId = readStoredValue(ACTIVE_DOCUMENT_KEY, "");
        const editableDocuments = localDocuments.filter(
          (document) => getDocumentStatus(document) !== "trashed"
        );
        const selectedDocument =
          editableDocuments.find((document) => document.id === activeDocumentId) ??
          editableDocuments[0] ??
          localDocuments[0];

        if (!selectedDocument || cancelled) {
          return;
        }

        documentsRef.current = localDocuments;
        documentsReadyRef.current = true;
        setDocuments(localDocuments);
        setCurrentDocumentId(selectedDocument.id);
        setDocumentTitle(selectedDocument.title);
        setDocumentText(selectedDocument.text);
        setDocumentsReady(true);
        writeStoredValue(ACTIVE_DOCUMENT_KEY, selectedDocument.id);
      } catch (error) {
        if (!cancelled) {
          setLibraryError(error instanceof Error ? error.message : String(error));
        }
      }
    };

    void loadLocalDocuments();

    return () => {
      cancelled = true;
    };
  }, []);

  const postToWorker = useCallback(
    (message: Parameters<Worker["postMessage"]>[0], transfer?: Transferable[]) => {
      workerRef.current?.postMessage(message, transfer ?? []);
    },
    []
  );

  const prepareVoice = useCallback(() => {
    if (preparedRef.current) {
      return;
    }

    preparedRef.current = true;
    setWorkerError("");
    postToWorker({
      type: "load",
      config: modelConfig
    });
  }, [modelConfig, postToWorker]);

  const appendToDocument = useCallback(
    (text: string, appendMode: AppendMode = "paragraph") => {
      const cleaned = text.trim();
      if (!cleaned || cleaned === "[no speech detected]") {
        return;
      }

      setDocumentText((previous) => {
        if (!previous.trim()) {
          return cleaned;
        }

        return shouldAppendInline(previous, cleaned, appendMode)
          ? joinTranscriptText(previous, cleaned)
          : `${previous.trimEnd()}\n\n${cleaned}`;
      });
    },
    [setDocumentText]
  );

  const saveDocumentToStorage = useCallback(async (document: LocalDocument) => {
    if (storageModeRef.current === "drive") {
      const savedDocument = await saveDriveDocument(document);
      void saveDocument(savedDocument).catch(() => {
        // Local cache writes are best-effort when Drive is the source of truth.
      });
      return savedDocument;
    }

    await saveDocument(document);
    return document;
  }, []);

  const deleteDocumentFromStorage = useCallback(async (document: LocalDocument) => {
    if (storageModeRef.current === "drive") {
      await deleteDriveDocument(document);
    }

    await deleteDocument(document.id);
  }, []);

  const deleteDocumentsFromStorage = useCallback(async (documentsToDelete: LocalDocument[]) => {
    if (storageModeRef.current === "drive") {
      await deleteDriveDocuments(documentsToDelete);
    }

    await deleteDocuments(documentsToDelete.map((document) => document.id));
  }, []);

  const persistCurrentDocument = useCallback(async () => {
    if (!documentsReadyRef.current || !currentDocumentId) {
      return;
    }

    const existingDocument = documentsRef.current.find(
      (document) => document.id === currentDocumentId
    );
    const nextDocument = createLocalDocument({
      ...existingDocument,
      id: currentDocumentId,
      title: documentTitle,
      text: documentText,
      createdAt: existingDocument?.createdAt,
      updatedAt: Date.now()
    });

    documentsRef.current = [
      nextDocument,
      ...documentsRef.current.filter((document) => document.id !== currentDocumentId)
    ].sort((left, right) => right.updatedAt - left.updatedAt);
    setDocuments(documentsRef.current);
    const savedDocument = await saveDocumentToStorage(nextDocument);
    documentsRef.current = [
      savedDocument,
      ...documentsRef.current.filter((document) => document.id !== currentDocumentId)
    ].sort((left, right) => right.updatedAt - left.updatedAt);
    setDocuments(documentsRef.current);
  }, [currentDocumentId, documentText, documentTitle, saveDocumentToStorage]);

  const applyCurrentDocument = useCallback((document: LocalDocument) => {
    writeStoredValue(ACTIVE_DOCUMENT_KEY, document.id);
    setCurrentDocumentId(document.id);
    setDocumentTitle(document.title);
    setDocumentText(document.text);
  }, []);

  const ensureEditableCurrentDocument = useCallback(
    async (nextDocuments: LocalDocument[]) => {
      const currentDocumentStillEditable = nextDocuments.some(
        (document) =>
          document.id === currentDocumentId && getDocumentStatus(document) !== "trashed"
      );

      if (currentDocumentStillEditable) {
        return nextDocuments;
      }

      const fallbackDocument = nextDocuments.find(
        (document) => getDocumentStatus(document) !== "trashed"
      );

      if (fallbackDocument) {
        applyCurrentDocument(fallbackDocument);
        return nextDocuments;
      }

      const blankDocument = createLocalDocument();
      const savedBlankDocument = await saveDocumentToStorage(blankDocument);
      const withBlankDocument = [savedBlankDocument, ...nextDocuments].sort(
        (left, right) => right.updatedAt - left.updatedAt
      );
      applyCurrentDocument(savedBlankDocument);
      return withBlankDocument;
    },
    [applyCurrentDocument, currentDocumentId, saveDocumentToStorage]
  );

  useEffect(() => {
    if (!documentsReady || !currentDocumentId) {
      return;
    }

    const existingDocument = documentsRef.current.find(
      (document) => document.id === currentDocumentId
    );
    const nextDocument = createLocalDocument({
      ...existingDocument,
      id: currentDocumentId,
      title: documentTitle,
      text: documentText,
      createdAt: existingDocument?.createdAt,
      updatedAt: Date.now()
    });

    documentsRef.current = documentsRef.current
      .map((document) => (document.id === currentDocumentId ? nextDocument : document))
      .sort((left, right) => right.updatedAt - left.updatedAt);
    setDocuments(documentsRef.current);

    const saveTimer = window.setTimeout(() => {
      void saveDocumentToStorage(nextDocument)
        .then((savedDocument) => {
          if (savedDocument.driveFileId === nextDocument.driveFileId) {
            return;
          }

          documentsRef.current = documentsRef.current
            .map((document) => (document.id === currentDocumentId ? savedDocument : document))
            .sort((left, right) => right.updatedAt - left.updatedAt);
          setDocuments(documentsRef.current);
        })
        .catch((error: unknown) => {
          setLibraryError(error instanceof Error ? error.message : String(error));
        });
    }, storageModeRef.current === "drive" ? 1200 : 350);

    return () => {
      window.clearTimeout(saveTimer);
    };
  }, [currentDocumentId, documentText, documentTitle, documentsReady, saveDocumentToStorage]);

  const scrollDocumentToEnd = useCallback(() => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const editor = documentRef.current;
        if (!editor) {
          return;
        }

        editor.focus();
        editor.setSelectionRange(editor.value.length, editor.value.length);
        stageRef.current?.scrollTo({
          top: stageRef.current.scrollHeight,
          behavior: "smooth"
        });
      });
    });
  }, []);

  useEffect(() => {
    const editor = documentRef.current;
    if (!editor) {
      return;
    }

    editor.style.height = "auto";
    editor.style.height = `${Math.max(editor.scrollHeight, window.innerHeight - 210)}px`;
  }, [documentText]);

  const markSettled = useCallback((id?: string) => {
    if (id && pendingIdsRef.current.delete(id)) {
      segmentAppendModeRef.current.delete(id);
      setPendingCount(pendingIdsRef.current.size);
    }
  }, []);

  useEffect(
    () => () => {
      if (silenceTimerRef.current !== undefined) {
        window.clearTimeout(silenceTimerRef.current);
      }

      if (noSpeechTimerRef.current !== undefined) {
        window.clearTimeout(noSpeechTimerRef.current);
      }

      if (recordingLimitTimerRef.current !== undefined) {
        window.clearInterval(recordingLimitTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const worker = new Worker(new URL("./whisper.worker.ts", import.meta.url), {
      type: "module"
    });
    workerRef.current = worker;

    worker.onmessage = (event: MessageEvent<WorkerOutboundMessage>) => {
      const message = event.data;

      if (message.type === "model-loading") {
        setWorkerPhase("loading");
        setWorkerError("");
      }

      if (message.type === "model-ready") {
        setWorkerPhase("ready");
      }

      if (message.type === "transcription-start") {
        setWorkerPhase("transcribing");
      }

      if (message.type === "transcription-result") {
        const appendMode = segmentAppendModeRef.current.get(message.id) ?? "paragraph";
        markSettled(message.id);
        setWorkerPhase("ready");
        appendToDocument(message.text, appendMode);
        scrollDocumentToEnd();
        setTranscripts((items) => [
          {
            id: message.id,
            sequence: message.sequence,
            text: message.text || "[no speech detected]",
            durationSeconds: message.durationSeconds,
            inferenceSeconds: message.inferenceSeconds,
            rms: message.rms,
            createdAt: Date.now()
          },
          ...items.slice(0, 5)
        ]);
      }

      if (message.type === "worker-error") {
        markSettled(message.id);
        preparedRef.current = false;
        setWorkerPhase("error");
        setWorkerError(message.message);
      }
    };

    window.requestIdleCallback?.(() => prepareVoice());
    if (!("requestIdleCallback" in window)) {
      globalThis.setTimeout(prepareVoice, 500);
    }

    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, [appendToDocument, markSettled, prepareVoice, scrollDocumentToEnd]);

  const recordChunk = useCallback((chunk: AudioChunk) => {
    if (!micActiveRef.current && !finishingRef.current) {
      return;
    }

    const samples = resampleTo16Khz(chunk.samples, chunk.sampleRate);
    recordedChunksRef.current.push({
      ...chunk,
      sampleRate: 16_000,
      samples,
      durationSeconds: samples.length / 16_000
    });
  }, []);

  const transcribeRecording = useCallback(
    (chunks: AudioChunk[]) => {
      const segments = buildTranscriptionSegments(chunks);

      for (const segment of segments) {
        const id =
          globalThis.crypto?.randomUUID?.() ??
          `${Date.now()}-${segmentSequenceRef.current}-${Math.random().toString(16).slice(2)}`;
        const sequence = ++segmentSequenceRef.current;

        pendingIdsRef.current.add(id);
        segmentAppendModeRef.current.set(id, segment.appendMode);
        setPendingCount(pendingIdsRef.current.size);

        postToWorker(
          {
            type: "transcribe",
            id,
            sequence,
            audio: segment.samples,
            durationSeconds: segment.durationSeconds,
            rms: segment.rms,
            config: modelConfig
          },
          [segment.samples.buffer]
        );
      }
    },
    [modelConfig, postToWorker]
  );

  const clearVoiceTimers = useCallback(() => {
    if (silenceTimerRef.current !== undefined) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = undefined;
    }

    if (noSpeechTimerRef.current !== undefined) {
      window.clearTimeout(noSpeechTimerRef.current);
      noSpeechTimerRef.current = undefined;
    }

    if (recordingLimitTimerRef.current !== undefined) {
      window.clearInterval(recordingLimitTimerRef.current);
      recordingLimitTimerRef.current = undefined;
    }

    setRemainingRecordingSeconds(null);
  }, []);

  const finishDictation = useCallback(async () => {
    if (finishingRef.current) {
      return;
    }

    finishingRef.current = true;
    clearVoiceTimers();
    speechHeardRef.current = false;

    await recorderRef.current?.stop();
    recorderRef.current = null;
    micActiveRef.current = false;
    setMicActive(false);
    setLevel(0);

    const chunks = recordedChunksRef.current;
    recordedChunksRef.current = [];
    finishingRef.current = false;
    transcribeRecording(chunks);
  }, [clearVoiceTimers, transcribeRecording]);

  const handleVoiceLevel = useCallback(
    (rms: number) => {
      setLevel(rmsToPercent(rms));

      if (!micActiveRef.current) {
        return;
      }

      if (rms >= SPEECH_RMS_THRESHOLD) {
        speechHeardRef.current = true;

        if (silenceTimerRef.current !== undefined) {
          window.clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = undefined;
        }

        if (noSpeechTimerRef.current !== undefined) {
          window.clearTimeout(noSpeechTimerRef.current);
          noSpeechTimerRef.current = undefined;
        }

        return;
      }

      if (speechHeardRef.current && silenceTimerRef.current === undefined) {
        silenceTimerRef.current = window.setTimeout(() => {
          void finishDictation();
        }, AUTO_FINISH_SILENCE_MS);
      }
    },
    [finishDictation]
  );

  const startMic = useCallback(async () => {
    setMicError("");
    setWorkerError("");
    prepareVoice();
    clearVoiceTimers();
    speechHeardRef.current = false;
    recordedChunksRef.current = [];
    finishingRef.current = false;

    const recorder = new PcmChunkRecorder();
    recorderRef.current = recorder;

    try {
      await recorder.start({
        chunkSeconds: RECORDING_SLICE_SECONDS,
        onChunk: recordChunk,
        onLevel: handleVoiceLevel
      });
      micActiveRef.current = true;
      setMicActive(true);
      setRemainingRecordingSeconds(MAX_RECORDING_SECONDS);
      const startedAt = performance.now();
      recordingLimitTimerRef.current = window.setInterval(() => {
        const elapsedSeconds = (performance.now() - startedAt) / 1000;
        const remainingSeconds = Math.max(0, MAX_RECORDING_SECONDS - elapsedSeconds);
        setRemainingRecordingSeconds(Math.ceil(remainingSeconds));

        if (remainingSeconds <= 0) {
          void finishDictation();
        }
      }, 250);
      noSpeechTimerRef.current = window.setTimeout(() => {
        if (!speechHeardRef.current) {
          void finishDictation();
        }
      }, NO_SPEECH_TIMEOUT_MS);
      documentRef.current?.focus();
    } catch (error) {
      recorderRef.current = null;
      micActiveRef.current = false;
      clearVoiceTimers();
      setMicError(error instanceof Error ? error.message : String(error));
    }
  }, [clearVoiceTimers, finishDictation, handleVoiceLevel, prepareVoice, recordChunk]);

  const stopMic = useCallback(async () => {
    await finishDictation();
  }, [finishDictation]);

  const openLibrary = useCallback(() => {
    if (micActiveRef.current) {
      void finishDictation();
    }

    void persistCurrentDocument();
    setLibraryView("documents");
    setSearchTerm("");
    setViewMode("library");
  }, [finishDictation, persistCurrentDocument]);

  const openEditor = useCallback(async () => {
    const nextDocuments = await ensureEditableCurrentDocument(documentsRef.current);
    documentsRef.current = nextDocuments;
    setDocuments(nextDocuments);
    setViewMode("editor");
  }, [ensureEditableCurrentDocument]);

  const openDocument = useCallback(
    async (document: LocalDocument) => {
      if (micActiveRef.current) {
        await finishDictation();
      }

      await persistCurrentDocument();
      applyCurrentDocument(document);
      setViewMode("editor");
    },
    [applyCurrentDocument, finishDictation, persistCurrentDocument]
  );

  const createNewDocument = useCallback(async () => {
    if (micActiveRef.current) {
      await finishDictation();
    }

    await persistCurrentDocument();
    const nextDocument = createLocalDocument();
    const savedDocument = await saveDocumentToStorage(nextDocument);
    const nextDocuments = [savedDocument, ...documentsRef.current].sort(
      (left, right) => right.updatedAt - left.updatedAt
    );

    documentsRef.current = nextDocuments;
    setDocuments(nextDocuments);
    setCurrentDocumentId(savedDocument.id);
    setDocumentTitle(savedDocument.title);
    setDocumentText(savedDocument.text);
    setSearchTerm("");
    setLibraryView("documents");
    setViewMode("editor");
    writeStoredValue(ACTIVE_DOCUMENT_KEY, savedDocument.id);
  }, [finishDictation, persistCurrentDocument, saveDocumentToStorage]);

  const exportDocument = useCallback(() => {
    const blob = new Blob([documentText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const fallbackTitle = currentDocument ? getDocumentDisplayTitle(currentDocument) : "whisperwriter";
    const safeTitle = safeFileName(documentTitle.trim() || fallbackTitle);
    link.href = url;
    link.download = `${safeTitle}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  }, [currentDocument, documentText, documentTitle]);

  const exportAllDocuments = useCallback(() => {
    const exportDocuments = documentsRef.current.length > 0 ? documentsRef.current : documents;
    const payload = {
      exportedAt: new Date().toISOString(),
      documents: exportDocuments.map((document) => ({
        id: document.id,
        title: document.title,
        displayTitle: getDocumentDisplayTitle(document),
        status: getDocumentStatus(document),
        text: document.text,
        createdAt: new Date(document.createdAt).toISOString(),
        updatedAt: new Date(document.updatedAt).toISOString()
      }))
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json;charset=utf-8"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `whisperwriter-documents-${formatExportDate()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [documents]);

  const moveDocumentToStatus = useCallback(
    async (document: LocalDocument, status: DocumentStatus) => {
      await persistCurrentDocument();

      const now = Date.now();
      const nextDocument = createLocalDocument({
        ...document,
        status,
        archivedAt: status === "archived" ? now : undefined,
        trashedAt: status === "trashed" ? now : undefined,
        updatedAt: now
      });
      const nextDocuments = documentsRef.current
        .map((item) => (item.id === document.id ? nextDocument : item))
        .sort((left, right) => right.updatedAt - left.updatedAt);
      const editableDocuments = await ensureEditableCurrentDocument(nextDocuments);
      const savedDocument = await saveDocumentToStorage(nextDocument);
      const savedDocuments = editableDocuments
        .map((item) => (item.id === savedDocument.id ? savedDocument : item))
        .sort((left, right) => right.updatedAt - left.updatedAt);

      documentsRef.current = savedDocuments;
      setDocuments(savedDocuments);
      setActionMenuDocumentId("");
    },
    [ensureEditableCurrentDocument, persistCurrentDocument, saveDocumentToStorage]
  );

  const permanentlyDeleteDocument = useCallback(
    async (document: LocalDocument) => {
      if (!window.confirm("Delete this document forever?")) {
        return;
      }

      const nextDocuments = documentsRef.current.filter((item) => item.id !== document.id);
      const editableDocuments = await ensureEditableCurrentDocument(nextDocuments);

      await deleteDocumentFromStorage(document);
      documentsRef.current = editableDocuments;
      setDocuments(editableDocuments);
      setActionMenuDocumentId("");
    },
    [deleteDocumentFromStorage, ensureEditableCurrentDocument]
  );

  const emptyTrash = useCallback(async () => {
    const trashIds = documentsRef.current
      .filter((document) => getDocumentStatus(document) === "trashed")
      .map((document) => document.id);

    if (trashIds.length === 0) {
      return;
    }

    const noun = trashIds.length === 1 ? "document" : "documents";
    if (!window.confirm(`Delete ${trashIds.length} ${noun} forever?`)) {
      return;
    }

    const trashDocuments = documentsRef.current.filter((document) => trashIds.includes(document.id));
    const nextDocuments = documentsRef.current.filter((document) => !trashIds.includes(document.id));
    const editableDocuments = await ensureEditableCurrentDocument(nextDocuments);

    await deleteDocumentsFromStorage(trashDocuments);
    documentsRef.current = editableDocuments;
    setDocuments(editableDocuments);
    setActionMenuDocumentId("");
  }, [deleteDocumentsFromStorage, ensureEditableCurrentDocument]);

  const connectDrive = useCallback(async () => {
    if (!isGoogleDriveConfigured(GOOGLE_CLIENT_ID) || driveStatus === "connecting") {
      return;
    }

    try {
      setLibraryError("");
      setDriveStatus("connecting");

      if (micActiveRef.current) {
        await finishDictation();
      }

      await persistCurrentDocument();
      const localDocuments = await listDocuments();

      await requestDriveAccess(GOOGLE_CLIENT_ID);
      let driveDocuments = await listDriveDocuments();

      if (driveDocuments.length === 0 && localDocuments.length > 0) {
        driveDocuments = await Promise.all(
          localDocuments.map((document) =>
            saveDriveDocument(
              createLocalDocument({
                ...document,
                driveFileId: undefined
              })
            )
          )
        );
        await Promise.all(
          driveDocuments.map((document) =>
            saveDocument(document).catch(() => {
              // Drive is authoritative after connect; local migration cache is best-effort.
            })
          )
        );
      }

      if (driveDocuments.length === 0) {
        const blankDocument = await saveDriveDocument(createLocalDocument());
        driveDocuments = [blankDocument];
        void saveDocument(blankDocument).catch(() => {
          // Drive is authoritative after connect; local cache is best-effort.
        });
      }

      driveDocuments = driveDocuments.sort((left, right) => right.updatedAt - left.updatedAt);
      storageModeRef.current = "drive";
      documentsRef.current = driveDocuments;
      documentsReadyRef.current = true;
      setStorageMode("drive");
      setDriveStatus("ready");
      setDocuments(driveDocuments);
      setDocumentsReady(true);

      const activeDocumentId = readStoredValue(ACTIVE_DOCUMENT_KEY, "");
      const selectedDocument =
        driveDocuments.find((document) => document.id === activeDocumentId) ??
        driveDocuments.find((document) => getDocumentStatus(document) !== "trashed") ??
        driveDocuments[0];

      if (selectedDocument) {
        applyCurrentDocument(selectedDocument);
      }
    } catch (error) {
      setDriveStatus(storageModeRef.current === "drive" ? "ready" : "available");
      setLibraryError(error instanceof Error ? error.message : String(error));
    }
  }, [applyCurrentDocument, driveStatus, finishDictation, persistCurrentDocument]);

  const toggleLibrary = useCallback(() => {
    if (viewMode === "library") {
      void openEditor();
      return;
    }

    openLibrary();
  }, [openEditor, openLibrary, viewMode]);

  const documentStatus = useMemo(() => {
    if (libraryError) {
      return "Sync error";
    }

    if (micError || workerError) {
      return "Voice error";
    }

    if (pendingCount > 0) {
      return "Writing";
    }

    return "";
  }, [libraryError, micError, pendingCount, workerError]);

  const driveButton =
    driveStatus !== "unavailable" ? (
      <button
        className={storageMode === "drive" ? "quiet-action active" : "quiet-action"}
        type="button"
        onClick={() => void connectDrive()}
        disabled={driveStatus === "connecting"}
        aria-label={storageMode === "drive" ? "Google Drive connected" : "Connect Google Drive"}
        title={storageMode === "drive" ? "Drive connected" : "Use Google Drive"}
      >
        {driveStatus === "connecting" ? (
          <LoaderCircle className="spin" size={18} />
        ) : (
          <Cloud size={18} />
        )}
      </button>
    ) : null;

  return (
    <main className="whisperwriter-app">
      <header className="top-chrome">
        <div className="brand">
          <button
            className={viewMode === "library" ? "folder-action active" : "folder-action"}
            type="button"
            onClick={toggleLibrary}
            aria-label={viewMode === "library" ? "Return to editor" : "Open documents"}
            title="Documents"
          >
            <Folder size={19} />
          </button>
          <span>WhisperWriter</span>
        </div>

        <div className="document-title-wrap">
          {viewMode === "library" ? (
            <div className="document-title-row">
              <span className="document-title-label">{getLibraryTitle(libraryView)}</span>
            </div>
          ) : (
            <div className="document-title-row">
              {micActive && <span className="recording-dot" aria-label="Listening" />}
              <input
                aria-label="Document title"
                placeholder={DEFAULT_TITLE}
                size={titleInputSize}
                value={documentTitle}
                onChange={(event) => setDocumentTitle(event.target.value)}
              />
            </div>
          )}
          {documentStatus && (
            <div className="save-state">
              {documentStatus}
            </div>
          )}
        </div>

        <div className="top-actions">
          {viewMode === "library" ? (
            <>
              {driveButton}
              <button
                className={libraryView === "archive" ? "quiet-action active" : "quiet-action"}
                type="button"
                onClick={() => setLibraryView(libraryView === "archive" ? "documents" : "archive")}
                aria-label="Open archive"
                title="Archive"
              >
                <Archive size={18} />
              </button>
              <button
                className={libraryView === "trash" ? "quiet-action active" : "quiet-action"}
                type="button"
                onClick={() => setLibraryView(libraryView === "trash" ? "documents" : "trash")}
                aria-label="Open trash folder"
                title="Trash folder"
              >
                <Trash2 size={18} />
              </button>
              <button
                className="quiet-action"
                type="button"
                onClick={() => void createNewDocument()}
                aria-label="New document"
                title="New document"
              >
                <SquarePen size={18} />
              </button>
            </>
          ) : (
            <>
              {driveButton}
              <button className="quiet-action" type="button" onClick={exportDocument} title="Export">
                <Download size={18} />
              </button>
              <button
                className="quiet-action"
                type="button"
                onClick={() => void createNewDocument()}
                aria-label="New document"
                title="New document"
              >
                <SquarePen size={18} />
              </button>
            </>
          )}
        </div>
      </header>

      {viewMode === "library" ? (
        <section className="library-stage" aria-label="Documents">
          <div className="library-shell">
            <div className="library-controls">
              <label className="search-field">
                <Search size={17} />
                <input
                  aria-label="Search documents"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </label>
            </div>

            <div className="document-grid">
              {filteredDocuments.map((document) => {
                const status = getDocumentStatus(document);

                return (
                  <div
                    className={
                      document.id === currentDocumentId ? "document-card active" : "document-card"
                    }
                    key={document.id}
                  >
                    <button
                      className="document-card-open"
                      type="button"
                      onClick={() => void openDocument(document)}
                    >
                      <span className="document-card-title">{getDocumentDisplayTitle(document)}</span>
                      <span className="document-card-date">
                        {formatDocumentDate(document.updatedAt)}
                      </span>
                      <span
                        className={
                          getDocumentPreview(document.text)
                            ? "document-card-preview"
                            : "document-card-preview empty"
                        }
                      >
                        {getDocumentPreview(document.text)}
                      </span>
                    </button>
                    <button
                      className="document-menu-trigger"
                      type="button"
                      aria-label={`Actions for ${getDocumentDisplayTitle(document)}`}
                      title="More"
                      onClick={(event) => {
                        event.stopPropagation();
                        setActionMenuDocumentId((currentId) =>
                          currentId === document.id ? "" : document.id
                        );
                      }}
                    >
                      <MoreHorizontal size={18} />
                    </button>
                    {actionMenuDocumentId === document.id && (
                      <div className="document-menu" role="menu" onClick={(event) => event.stopPropagation()}>
                        {status !== "active" && (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => void moveDocumentToStatus(document, "active")}
                          >
                            <RotateCcw size={15} />
                            Restore
                          </button>
                        )}
                        {status !== "archived" && status !== "trashed" && (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => void moveDocumentToStatus(document, "archived")}
                          >
                            <Archive size={15} />
                            Archive
                          </button>
                        )}
                        {status !== "trashed" ? (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => void moveDocumentToStatus(document, "trashed")}
                          >
                            <Trash2 size={15} />
                            Trash
                          </button>
                        ) : (
                          <button
                            className="danger-menu-item"
                            type="button"
                            role="menuitem"
                            onClick={() => void permanentlyDeleteDocument(document)}
                          >
                            <Trash2 size={15} />
                            Delete forever
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {documentsReady && filteredDocuments.length === 0 && (
                <div className="library-empty">No documents</div>
              )}
            </div>
          </div>
        </section>
      ) : (
        <section className="writing-stage" ref={stageRef}>
          <section className="paper-shell" aria-label="Document editor">
            <textarea
              ref={documentRef}
              aria-label="Document body"
              value={documentText}
              onChange={(event) => setDocumentText(event.target.value)}
              placeholder="Write or dictate..."
              spellCheck
            />
          </section>

          <div className={micActive ? "dictation-control active" : "dictation-control"}>
            <button
              className={voiceActionClass}
              type="button"
              onClick={micActive ? stopMic : startMic}
              aria-label={micActive ? "Finish dictation" : "Start dictation"}
            >
              {micActive ? <Square size={17} fill="currentColor" /> : <Mic size={20} />}
              {countdownSeconds !== null && (
                <span className="voice-countdown">{formatCountdown(countdownSeconds)}</span>
              )}
            </button>
          </div>
        </section>
      )}

      <footer className={viewMode === "library" ? "bottom-bar library-bottom-bar" : "bottom-bar"}>
        <div>
          <span>
            {viewMode === "library"
              ? getLibraryCountLabel(libraryView, libraryDocuments.length)
              : `${documentStats.words} words`}
          </span>
        </div>
        {viewMode === "editor" ? (
          <div className={micActive ? "voice-strip active" : "voice-strip"} aria-hidden={!micActive}>
            <div className="voice-meter" aria-label="Voice level">
              <span style={{ width: `${level}%` }} />
            </div>
          </div>
        ) : (
          <div />
        )}
        <div className="footer-actions">
          {viewMode === "library" && (
            libraryView === "trash" ? (
              <button
                className="footer-command danger-action"
                type="button"
                onClick={() => void emptyTrash()}
                disabled={trashedDocuments.length === 0}
              >
                <Trash2 size={15} />
                Empty trash
              </button>
            ) : (
              <button
                className="footer-command"
                type="button"
                onClick={exportAllDocuments}
                disabled={documents.length === 0}
              >
                <Download size={15} />
                Download all
              </button>
            )
          )}
        </div>
      </footer>

      {(micError || workerError || libraryError) && (
        <div className="voice-error" role="alert">
          {micError || workerError || libraryError}
        </div>
      )}

    </main>
  );
}
